import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Eye, EyeSlash } from "@phosphor-icons/react";
import API from "../lib/api";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      await API.post("/auth/reset-password", { token, new_password: password });
      setSuccess(true);
      setTimeout(() => navigate("/auth"), 2500);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
    setLoading(false);
  };

  if (!token) {
    return (
      <div className="min-h-screen pt-28 pb-24 px-6 flex items-center justify-center" data-testid="reset-password-page">
        <div className="text-center space-y-4">
          <p className="text-[#FDFBF7] text-sm font-light">This reset link is invalid or incomplete.</p>
          <Link to="/auth" className="text-[#D4AF37] hover:text-[#E5C158] text-sm transition-colors duration-300" data-testid="invalid-link-back">
            Back to Sign In
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-28 pb-24 px-6 flex items-center justify-center" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-12">
          <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-3 font-medium">Account Recovery</p>
          <h1 className="font-['Cormorant_Garamond'] text-4xl font-light text-[#FDFBF7] tracking-tighter">Reset Password</h1>
        </div>

        {success ? (
          <div className="text-center space-y-4" data-testid="reset-success-message">
            <p className="text-[#FDFBF7] text-sm font-light">Your password has been reset successfully.</p>
            <p className="text-[#A1A1AA] text-xs font-light">Redirecting you to sign in...</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="reset-password-form">
            <div className="relative">
              <label className="text-xs uppercase tracking-[0.15em] text-[#A1A1AA] mb-2 block font-medium">New Password</label>
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                minLength={6}
                className="w-full bg-transparent border-b border-white/20 px-0 py-3 text-sm text-[#FDFBF7] placeholder-zinc-500 focus:outline-none focus:border-[#D4AF37] transition-colors duration-300 font-light pr-10"
                placeholder="At least 6 characters"
                data-testid="reset-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-0 top-8 text-[#A1A1AA] hover:text-[#FDFBF7] transition-colors"
                data-testid="reset-toggle-visibility"
              >
                {showPassword ? <EyeSlash size={18} weight="thin" /> : <Eye size={18} weight="thin" />}
              </button>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.15em] text-[#A1A1AA] mb-2 block font-medium">Confirm Password</label>
              <input
                type={showPassword ? "text" : "password"}
                value={confirm}
                onChange={e => setConfirm(e.target.value)}
                required
                className="w-full bg-transparent border-b border-white/20 px-0 py-3 text-sm text-[#FDFBF7] placeholder-zinc-500 focus:outline-none focus:border-[#D4AF37] transition-colors duration-300 font-light"
                placeholder="Re-enter new password"
                data-testid="reset-confirm-input"
              />
            </div>

            {error && <p className="text-red-400 text-sm font-light" data-testid="reset-error">{error}</p>}

            <button
              type="submit"
              disabled={loading}
              className="btn-luxury w-full bg-[#D4AF37] text-black py-4 text-sm tracking-[0.15em] uppercase font-medium hover:bg-[#E5C158] transition-colors duration-500 disabled:opacity-50"
              data-testid="reset-submit-btn"
            >
              {loading ? "Resetting..." : "Reset Password"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
